import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { api, type ExplainDriver, type ExplainTeam } from '../api'
import { Detail } from './Detail'

/**
 * The evidence behind one prediction.
 *
 * Fetched only when opened — most readers want the number, and a request per card on a page of
 * twelve fixtures would cost far more than it returns.
 *
 * What this shows is the model's **inputs**, not a per-feature attribution. A gradient-boosted
 * ensemble has no honest "this feature contributed 12%" without computing SHAP values, and inventing
 * one would be worse than showing none: a confident explanation of a number is exactly the thing a
 * reader would take on trust. So the panel says "here is the evidence, ranked by what moves this
 * model in general", and lets the reader draw the line themselves.
 */
export function WhyPanel({ fixtureId, teamA, teamB }: {
  fixtureId: number
  teamA: string
  teamB: string
}) {
  const [open, setOpen] = useState(false)
  const { data, isPending, error } = useQuery({
    queryKey: ['explain', fixtureId],
    queryFn: () => api.explain(fixtureId),
    enabled: open,
    staleTime: 5 * 60_000,
  })

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        /*
         * Styled as a control, because it is the best thing on the card and read as a footer.
         *
         * Behind it are both teams' five players with their ratings, the spread, and the weakest
         * link called out — the evidence for the probability above, and the most distinctive thing
         * this product does. It was a 12px faint line at the bottom edge, indistinguishable from
         * the caveats around it, so the panel might as well not have existed.
         */
        className="flex w-full items-center justify-between gap-2 border-t border-line px-4 py-2.5 text-left text-sm text-accent transition-colors hover:bg-accent-bg"
      >
        {/*
          Named for what is behind it, rather than for a question.
          
          "Why this number?" asked the reader to guess WHICH number — the card carries a series
          percentage, a map percentage and four break-even prices — and "five players a side" was
          shorthand for a fact nobody had been told yet. What actually opens is our rating of the two
          teams: five ranked differences between them, and both rosters with a rating per player.
        */}
        <span>How we rate these teams</span>
        <span aria-hidden="true" className="text-xs text-ink-faint">
          player ratings ▾
        </span>
      </button>
    )
  }

  return (
    <div className="border-t border-line px-4 py-3">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-xs font-medium text-ink-soft">How we rate these teams</h4>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-xs text-ink-faint hover:text-ink"
        >
          Hide
        </button>
      </div>

      {isPending ? <p className="text-xs text-ink-faint">Working it out…</p> : null}
      {error ? <p className="text-xs text-danger">Could not load the reasoning.</p> : null}

      {data && !data.available ? (
        <p className="text-xs text-ink-faint">{data.reason}</p>
      ) : null}

      {data?.available && data.drivers && data.teams ? (
        <>
          <ul className="space-y-1.5">
            {data.drivers.map((driver) => (
              <Driver key={driver.label} driver={driver} teamA={teamA} teamB={teamB} />
            ))}
          </ul>

          <div className="mt-3 grid gap-3 border-t border-line pt-3 sm:grid-cols-2">
            {data.teams.map((team) => (
              <Roster key={team.team} team={team} />
            ))}
          </div>

          <p className="mt-3 text-xs text-ink-faint">
            These are the inputs, not a breakdown of the answer — a boosted-tree model has no honest
            per-feature share without far heavier machinery. Ratings come only from games played
            before this match.
          </p>
        </>
      ) : null}
    </div>
  )
}

function Driver({ driver, teamA, teamB }: { driver: ExplainDriver; teamA: string; teamB: string }) {
  const favoured =
    driver.favours === 'teamA' ? teamA : driver.favours === 'teamB' ? teamB : null
  const magnitude = Math.abs(driver.difference)

  return (
    <li className="flex items-baseline justify-between gap-3 text-xs">
      <span className="min-w-0">
        <span className="text-ink-soft">{driver.label}</span>
        <span className="ml-1.5 text-ink-faint">{driver.what}</span>
      </span>
      <span className="tabular shrink-0 whitespace-nowrap">
        {favoured ? (
          <>
            <span className="text-ink">
              {magnitude < 10 ? magnitude.toFixed(2) : magnitude.toFixed(0)}
            </span>
            <span className="ml-1 text-ink-faint">{driver.unit} →</span>{' '}
            <span className="text-accent">{favoured}</span>
          </>
        ) : (
          <span className="text-ink-faint">level</span>
        )}
      </span>
    </li>
  )
}

/**
 * One side's five players.
 *
 * The weakest rating is called out because it is a driver in its own right: five even players beat
 * one star and four passengers more often than the averages suggest, and a wide spread is the thing
 * a mean rating hides.
 */
function Roster({ team }: { team: ExplainTeam }) {
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between">
        <span className="truncate text-xs font-medium">{team.team}</span>
        <span className="tabular text-xs text-ink-faint">
          avg {team.meanElo.toFixed(0)} · spread {team.eloSpread.toFixed(0)}
        </span>
      </div>
      <ul className="space-y-0.5">
        {[...team.players]
          .sort((a, b) => b.elo - a.elo)
          .map((player) => (
            <li key={player.accountId} className="flex justify-between gap-2 text-xs">
              <span className="min-w-0 truncate text-ink-soft">
                {player.name ?? `#${player.accountId}`}
              </span>
              <Detail
                label={`${player.name ?? player.accountId} — rating detail`}
                className={`tabular shrink-0 ${
                  player.elo === team.weakestElo ? 'text-warn' : 'text-ink-faint'
                }`}
                heading={<span>{player.elo.toFixed(0)}</span>}
              >
                Rated over <strong className="text-ink">{player.games}</strong> games played before
                this match.
                {player.elo === team.weakestElo
                  ? ' The weakest of the five — five even players beat one star and four passengers more often than the averages suggest.'
                  : ''}
              </Detail>
            </li>
          ))}
      </ul>
    </div>
  )
}
